import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PhoneIcon as PhoneIconSolid, EnvelopeIcon as EnvelopeIconSolid } from '@heroicons/react/24/solid';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useContent } from '../ContentContext';
import { getIconComponent } from '../utils/iconMap';

const defaultNav = [
  { title: 'Home', link: '/home', icon: 'HomeIcon' },
  { title: 'About', link: '/about', icon: 'InformationCircleIcon' },
  { title: 'Highlights', link: '/highlights', icon: 'StarIcon' },
  { title: 'Amenities', link: '/amenities', icon: 'BuildingOfficeIcon' },
  { title: 'Gallery', link: '/gallery', icon: 'PhotoIcon' },
  { title: 'FAQ', link: '/faq', icon: 'QuestionMarkCircleIcon' },
  { title: 'Projects', link: '/projects', icon: 'RectangleStackIcon' },
  { title: 'Location', link: '/location', icon: 'MapPinIcon' },
];

const Header = () => {
  const { content } = useContent();
  const { messages } = { messages: content };
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filter out Contact menu item - will be accessed via Enquire Now button
  const navItems = ((content as any).navArray || defaultNav).filter(
    (item: any) => item.title.toLowerCase() !== 'contact'
  );

  const handleEnquireNow = () => {
    setMobileMenuOpen(false);
    navigate('/contact');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleNavClick = (link: string) => {
    setMobileMenuOpen(false);
    navigate(link);
  };

  return (
    <header className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
      {/* Top Bar */}
      <div className="bg-[#0A1D2D] text-white py-2 px-4 sm:px-10 flex justify-between items-center text-xs sm:text-sm font-medium">
        <div className="flex items-center space-x-4 sm:space-x-6">
          <a href={`tel:${messages.common.phone}`} className="flex items-center space-x-2 hover:text-[#EF5D22] transition-colors">
            <PhoneIconSolid className="w-3.5 h-3.5 text-[#EF5D22]" />
            <span className="inline">{messages.common.phone}</span>
          </a>
          <a href={`mailto:${messages.common.email}`} className="hidden sm:flex items-center space-x-2 hover:text-[#EF5D22] transition-colors">
            <EnvelopeIconSolid className="w-3.5 h-3.5 text-[#EF5D22]" />
            <span>{messages.common.email}</span>
          </a>
        </div>
        <div className="flex items-center space-x-3 sm:space-x-4">
          <a href={messages.common.social.facebook} target="_blank" rel="noopener noreferrer" title="Facebook" className="hover:text-[#EF5D22] transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a href={messages.common.social.instagram} target="_blank" rel="noopener noreferrer" title="Instagram" className="hover:text-[#EF5D22] transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.805.249 2.227.412.56.216.96.474 1.38.894.42.42.678.82.894 1.38.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.249 1.805-.412 2.227-.216.56-.474.96-.894 1.38-.42.42-.82.678-1.38.894-.422.164-1.057.36-2.227.413-1.266.057-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.805-.249-2.227-.412-.56-.216-.96-.474-1.38-.894-.42-.42-.678-.82-.894-1.38-.164-.422-.36-1.057-.413-2.227-.057-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.054-1.17.249-1.805.412-2.227.216-.56.474-.96.894-1.38.42-.42.82-.678 1.38-.894.422-.164 1.057-.36 2.227-.413 1.266-.057 1.646-.07 4.85-.07zM12 0C8.741 0 8.333.014 7.053.072 5.775.131 4.903.335 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.335 4.903.131 5.775.072 7.053.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.059 1.277.263 2.148.558 2.911.306.788.717 1.459 1.384 2.126s1.337 1.079 2.126 1.384c.763.295 1.634.499 2.911.558 1.28.059 1.689.072 4.947.072s3.667-.014 4.947-.072c1.277-.059 2.148-.263 2.911-.558.788-.306 1.459-.717 2.126-1.384s1.079-1.337 1.384-2.126c.295-.763.499-1.634.558-2.911.059-1.28.072-1.689.072-4.947s-.014-3.667-.072-4.947c-.059-1.277-.263-2.148-.558-2.911-.306-.788-.717-1.459-1.384-2.126s-1.337-1.079-2.126-1.384c-.763-.295-1.634-.499-2.911-.558C15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z"/></svg>
          </a>
          <a href={messages.common.social.whatsapp} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="hover:text-[#EF5D22] transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.438 9.889-9.886.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.894 4.44-9.899 9.888-.001 2.225.586 3.891 1.546 5.45l-.971 3.546 3.673-.963zm13.155-7.144c-.272-.136-1.613-.797-1.863-.888-.249-.091-.43-.136-.611.136-.182.273-.703.888-.862 1.069-.159.182-.317.204-.589.068-.272-.136-1.15-.424-2.191-1.354-.81-.722-1.356-1.615-1.515-1.887-.159-.272-.017-.42.119-.556.123-.122.272-.318.408-.477.136-.159.182-.272.272-.454.091-.182.045-.341-.023-.477-.068-.136-.611-1.477-.839-2.022-.222-.534-.467-.46-.611-.468-.141-.008-.302-.008-.462-.008-.161 0-.422.06-.644.302-.222.242-.848.832-.848 2.029 0 1.197.87 2.354.991 2.52.12.167 1.711 2.613 4.14 3.662.578.249 1.028.397 1.38.509.58.185 1.109.158 1.527.096.466-.07 1.613-.659 1.841-1.295.227-.636.227-1.181.159-1.295-.068-.113-.249-.182-.522-.318z"/></svg>
          </a>
          <a href={messages.common.social.telegram} target="_blank" rel="noopener noreferrer" title="Telegram" className="hover:text-[#EF5D22] transition-colors">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.13-.31-1.08-.66.02-.18.27-.36.74-.55 2.91-1.27 4.85-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .33z"/></svg>
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="flex justify-between items-center py-3 sm:py-4 px-4 sm:px-10">
        <Link to="/" className="flex items-center">
          <img 
            src={(content as any).logo_url || '/images/mainLogo.png'} 
            alt={messages.common.companyName} 
            className="h-10 sm:h-12 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex space-x-6 xl:space-x-8 items-center text-[13px] font-bold text-gray-800">
          {navItems.map((item: any) => {
            const IconComp = getIconComponent(item.icon);
            return (
              <Link key={item.title} to={item.link} className="flex items-center space-x-1 hover:text-[#EF5D22] transition-colors">
                <IconComp className="w-4 h-4" />
                <span>{item.title}</span>
              </Link>
            );
          })}
          <button 
            onClick={handleEnquireNow}
            className="bg-[#EF5D22] text-white px-6 py-2 rounded-lg font-bold shadow-lg hover:bg-[#d9541a] transition-all transform hover:scale-105 ml-4"
          >
            {messages.common.enquireNow}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3 lg:hidden">
          <button 
            onClick={handleEnquireNow}
            className="bg-[#EF5D22] text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg"
          >
            Enquire
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6 text-gray-800" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-gray-800" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <img 
                src={(content as any).logo_url || '/images/mainLogo.png'} 
                alt={messages.common.companyName} 
                className="h-10 w-auto"
              />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="w-6 h-6 text-gray-800" />
              </button>
            </div>

            {/* Mobile Menu Items */}
            <div className="py-4">
              {navItems.map((item: any) => {
                const IconComp = getIconComponent(item.icon);
                return (
                  <button
                    key={item.title}
                    onClick={() => handleNavClick(item.link)}
                    className="w-full flex items-center space-x-3 px-6 py-4 text-gray-800 hover:bg-gray-50 hover:text-[#EF5D22] transition-colors text-left"
                  >
                    <IconComp className="w-5 h-5" />
                    <span className="font-semibold">{item.title}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile Menu Footer */}
            <div className="p-4 border-t border-gray-100 space-y-4">
              <button 
                onClick={handleEnquireNow}
                className="w-full bg-[#EF5D22] text-white py-3 rounded-xl font-bold shadow-lg hover:bg-[#d9541a] transition-all"
              >
                {messages.common.enquireNow}
              </button>
              
              {/* Contact Info */}
              <div className="space-y-3 text-sm">
                <a href={`tel:${messages.common.phone}`} className="flex items-center space-x-3 text-gray-600 hover:text-[#EF5D22]">
                  <PhoneIconSolid className="w-4 h-4 text-[#EF5D22]" />
                  <span>{messages.common.phone}</span>
                </a>
                <a href={`mailto:${messages.common.email}`} className="flex items-center space-x-3 text-gray-600 hover:text-[#EF5D22]">
                  <EnvelopeIconSolid className="w-4 h-4 text-[#EF5D22]" />
                  <span className="truncate">{messages.common.email}</span>
                </a>
              </div>

              {/* Social Icons */}
              <div className="flex items-center justify-center space-x-4 pt-2">
                <a href={messages.common.social.facebook} target="_blank" rel="noopener noreferrer" title="Facebook" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#EF5D22] hover:text-white transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href={messages.common.social.instagram} target="_blank" rel="noopener noreferrer" title="Instagram" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#EF5D22] hover:text-white transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.805.249 2.227.412.56.216.96.474 1.38.894.42.42.678.82.894 1.38.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.249 1.805-.412 2.227-.216.56-.474.96-.894 1.38-.42.42-.82.678-1.38.894-.422.164-1.057.36-2.227.413-1.266.057-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.805-.249-2.227-.412-.56-.216-.96-.474-1.38-.894-.42-.42-.678-.82-.894-1.38-.164-.422-.36-1.057-.413-2.227-.057-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.054-1.17.249-1.805.412-2.227.216-.56.474-.96.894-1.38.42-.42.82-.678 1.38-.894.422-.164 1.057-.36 2.227-.413 1.266-.057 1.646-.07 4.85-.07zM12 0C8.741 0 8.333.014 7.053.072 5.775.131 4.903.335 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.335 4.903.131 5.775.072 7.053.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.059 1.277.263 2.148.558 2.911.306.788.717 1.459 1.384 2.126s1.337 1.079 2.126 1.384c.763.295 1.634.499 2.911.558 1.28.059 1.689.072 4.947.072s3.667-.014 4.947-.072c1.277-.059 2.148-.263 2.911-.558.788-.306 1.459-.717 2.126-1.384s1.079-1.337 1.384-2.126c.295-.763.499-1.634.558-2.911.059-1.28.072-1.689.072-4.947s-.014-3.667-.072-4.947c-.059-1.277-.263-2.148-.558-2.911-.306-.788-.717-1.459-1.384-2.126s-1.337-1.079-2.126-1.384c-.763-.295-1.634-.499-2.911-.558C15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z"/></svg>
                </a>
                <a href={messages.common.social.whatsapp} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#EF5D22] hover:text-white transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.438 9.889-9.886.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.894 4.44-9.899 9.888-.001 2.225.586 3.891 1.546 5.45l-.971 3.546 3.673-.963zm13.155-7.144c-.272-.136-1.613-.797-1.863-.888-.249-.091-.43-.136-.611.136-.182.273-.703.888-.862 1.069-.159.182-.317.204-.589.068-.272-.136-1.15-.424-2.191-1.354-.81-.722-1.356-1.615-1.515-1.887-.159-.272-.017-.42.119-.556.123-.122.272-.318.408-.477.136-.159.182-.272.272-.454.091-.182.045-.341-.023-.477-.068-.136-.611-1.477-.839-2.022-.222-.534-.467-.46-.611-.468-.141-.008-.302-.008-.462-.008-.161 0-.422.06-.644.302-.222.242-.848.832-.848 2.029 0 1.197.87 2.354.991 2.52.12.167 1.711 2.613 4.14 3.662.578.249 1.028.397 1.38.509.58.185 1.109.158 1.527.096.466-.07 1.613-.659 1.841-1.295.227-.636.227-1.181.159-1.295-.068-.113-.249-.182-.522-.318z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
